import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  ConfirmDialog,
  DataGrid,
  StatusBadge,
  useToast,
  type DataGridColumn,
} from '@kss/ui';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { encodeId } from '../../utils/idCodec';
import { AddDropdown, RefreshButton } from '../shared/AddDropdown';
import type { PageContent, PageContentCreate } from '@kss/api';
import { pageContentService } from '@kss/api';
import { useModulePerms } from '../../hooks/useModulePerms';

/**
 * Skeleton rows the "Create defaults" action inserts on first mount when
 * the tenant has zero PageContent rows. Each row lands active so the
 * storefront starts rendering immediately — the admin can then edit the
 * title / body / meta inline. Deliberately terse content (not marketing
 * copy) because production admins usually rewrite it anyway; the goal is
 * "no crashed page while I'm typing", not "give them polished defaults".
 */
const DEFAULT_ROWS: PageContentCreate[] = [
  {
    slug: 'our-story',
    title: 'Our story',
    body:
      '<p>Welcome to our store. We are still adding the finishing touches to our story — check back soon.</p>',
    isActive: true,
  },
  {
    slug: 'contact-us',
    title: 'Contact us',
    body:
      '<p>Reach out any time — we usually reply within one business day.</p>',
    contactPhone: '',
    contactEmail: '',
    contactAddress: '',
    contactWhatsapp: '',
    isActive: true,
  },
  {
    slug: 'shipping',
    title: 'Shipping information',
    body: '<p>Standard shipping details will be published here shortly.</p>',
    isActive: true,
  },
  {
    slug: 'returns',
    title: 'Returns & refunds',
    body: '<p>Our returns policy will be posted here shortly.</p>',
    isActive: true,
  },
  {
    slug: 'faq',
    title: 'Frequently asked questions',
    body: '<p>Answers to common questions will land here.</p>',
    isActive: true,
  },
];

export default function AllPageContents() {
  const navigate = useNavigate();
  const toast = useToast();
  const perms = useModulePerms('PageContent');

  const [rows, setRows] = useState<PageContent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<PageContent | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pageContentService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load pages';
      setError(msg);
      toastRef.current.error('Load failed', msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete?.pageContentId) return;
    setDeleting(true);
    try {
      await pageContentService.delete(confirmDelete.pageContentId);
      toast.success('Deleted', `${confirmDelete.title} removed`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error('Delete failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleSeedDefaults = async () => {
    setSeeding(true);
    try {
      await pageContentService.create(DEFAULT_ROWS);
      toast.success('Defaults created', `${DEFAULT_ROWS.length} skeleton pages added`);
      await load();
    } catch (e) {
      toast.error('Seed failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSeeding(false);
    }
  };

  const columns: DataGridColumn<PageContent>[] = [
    {
      accessor: 'slug',
      title: 'Slug',
      sortable: true,
      filterable: true,
      render: (r) => (
        <code className="rounded bg-surface-card-hover px-1.5 py-0.5 font-mono text-xs">
          {r.slug}
        </code>
      ),
    },
    { accessor: 'title', title: 'Title', sortable: true, filterable: true },
    {
      accessor: 'isActive',
      title: 'Status',
      sortable: true,
      render: (r) => <StatusBadge status={r.isActive ? 'active' : 'inactive'} />,
    },
    {
      accessor: 'updatedAt',
      title: 'Updated',
      sortable: true,
      render: (r) => {
        const iso = r.updatedAt ?? r.createdAt;
        if (!iso) return '—';
        try {
          return new Date(iso).toLocaleDateString();
        } catch {
          return '—';
        }
      },
    },
    {
      accessor: 'pageContentId',
      title: 'Actions',
      width: 110,
      render: (r) => (
        <div className="flex items-center gap-1">
          {perms.canUpdate && (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
              onClick={(e) => {
                e.stopPropagation();
                if (r.pageContentId) navigate(`/panel/pages/${encodeId(r.pageContentId)}`);
              }}
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {perms.canDelete && (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDelete(r);
              }}
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ),
    },
  ];

  // Seed-defaults chrome only surfaces when the tenant genuinely has zero
  // rows. Hidden while the initial load is still in flight so we don't
  // suggest seeding a tenant that already has content.
  const showSeed = !loading && rows.length === 0;

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {showSeed && perms.canCreate && (
        <Alert variant="info" className="mb-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              No pages yet. Add skeleton content for Our Story, Contact Us,
              Shipping, Returns and FAQ so the storefront has something to render.
            </span>
            <Button size="sm" onClick={handleSeedDefaults} disabled={seeding}>
              {seeding ? 'Creating…' : 'Create defaults'}
            </Button>
          </div>
        </Alert>
      )}

      <DataGrid<PageContent>
        title={<span className="hidden sm:inline">All pages</span>}
        columnPicker
        onRowClick={(row) => {
          if (row.pageContentId) navigate(`/panel/pages/${encodeId(row.pageContentId)}`);
        }}
        data={rows}
        columns={columns}
        selectable
        selectedIds={selected}
        onSelectedChange={setSelected}
        defaultPageSize={10}
        pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <RefreshButton onClick={load} loading={loading} />
            {perms.canCreate && (
              <AddDropdown
                items={[
                  {
                    label: 'New page',
                    to: '/panel/pages/new',
                    icon: <Plus className="h-3.5 w-3.5" />,
                  },
                ]}
              />
            )}
          </div>
        }
      />

      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        onConfirm={handleDelete}
        title="Delete page"
        message={
          confirmDelete
            ? `This will permanently remove "${confirmDelete.title}". This action cannot be undone.`
            : ''
        }
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
      />
    </div>
  );
}
